// components/connexion/contenusConnexion.js
// ============================================================================
// CONTENUS DE LA PAGE DE CONNEXION
// ============================================================================
//
// Les textes se règlent depuis le CIK (« Page de connexion KLASYS ») et
// arrivent par `GET /api/public/page-connexion`. Ils s'affichent en trois temps,
// comme sur HoraPilot, pour que la page ne clignote jamais :
//
//   1. les valeurs ci-dessous, écrites dans le code — la page est complète même
//      si le CIK n'a rien réglé, ou si la route ne répond pas ;
//   2. la dernière réponse gardée dans le navigateur, propre à CETTE école ;
//   3. la réponse du serveur, qui remplace sans bruit et est gardée pour la
//      prochaine visite.
//
// Ces valeurs sont les mêmes que celles posées par la migration CIK 016.
// ============================================================================

import { getAuthHeaders } from '../../services/api';
import { tenantDeLHote } from '../../tenantFetch';
import { COULEURS_PAR_DEFAUT as COULEURS_CONNEXION_PAR_DEFAUT } from './couleursConnexion';

export const CONTENUS_PAR_DEFAUT = {
  marque: { nom: 'KLASYS', sous_titre: 'GESTION SCOLAIRE' },
  textes: [
    {
      titre: "L'école se pilote\nd'un seul regard",
      sous_titre: "De la rentrée à la clôture de l'année : inscriptions, classes, bulletins et encaissements, au même endroit."
    },
    {
      titre: 'Chaque rentrée,\nbien préparée',
      sous_titre: 'Classes, tarifs et calendrier se règlent avant le premier jour.'
    },
    {
      titre: "Pensé pour\nles écoles d'ici",
      sous_titre: 'Le franc CFA, les examens nationaux et le calendrier de votre pays.'
    },
    {
      titre: "Sécurisé,\njusqu'au dernier compte",
      sous_titre: 'Double authentification, droits fins et journal de chaque action.'
    }
  ],
  // `couleurs` : les teintes de la page du personnel, réglables depuis le CIK
  // comme celles des parents. Les valeurs d'origine vivent dans
  // `connexion/couleursConnexion.js` — un seul endroit pour « revenir aux
  // couleurs d'origine », des deux côtés.
  connexion: {
    titre: 'Connexion',
    sous_titre: "Choisissez l'année puis identifiez-vous",
    couleurs: { ...COULEURS_CONNEXION_PAR_DEFAUT }
  },
  pied: {
    copyright: 'KLASYS est une solution de gestion scolaire éditée par Daniced Group · Tous droits de reproduction interdits',
    liens: [
      { libelle: 'www.danicedgroup.com', cible: 'https://www.danicedgroup.com' },
      { libelle: 'Aide et contact', cible: '#aide' },
      { libelle: "Conditions d'utilisation", cible: '#cgu' },
      { libelle: 'Politique de confidentialité', cible: '#confidentialite' }
    ]
  },
  popups: {
    aide: {
      titre: 'Aide et contact',
      contenu: "Pour toute question sur l'utilisation de KLASYS, adressez-vous d'abord à l'administration de votre établissement.\n\nSi le problème persiste, le support KLASYS est joignable par l'intermédiaire de votre établissement."
    },
    cgu: {
      titre: "Conditions d'utilisation",
      contenu: "L'accès à KLASYS est réservé au personnel autorisé de l'établissement.\n\nChaque compte est personnel : identifiant et mot de passe ne se prêtent pas."
    },
    confidentialite: {
      titre: 'Politique de confidentialité',
      contenu: "Les données des élèves, des familles et du personnel appartiennent à l'établissement. Elles ne sont ni revendues ni communiquées à des tiers."
    },
    mot_de_passe: {
      titre: 'Mot de passe oublié',
      contenu: "Pour des raisons de sécurité, un mot de passe ne se réinitialise pas depuis cette page.\n\nAdressez-vous à l'administration de votre établissement : elle fera la demande dans le Centre de support, et un nouveau mot de passe temporaire vous sera remis. Son changement vous sera demandé à la première connexion."
    },
    support: {
      titre: 'Contacter le support',
      contenu: "Le support KLASYS accompagne les administrateurs de votre établissement.\n\nDécrivez-leur votre difficulté : ils la transmettront avec le nom de l'école et l'écran concerné."
    }
  },

  // ------------------------------------------------------------------------
  // L'espace famille : les deux pages que voient les parents
  // ------------------------------------------------------------------------
  // Réglées depuis le CIK (écran « Espace famille »), reprises ici pour que la
  // page tienne debout sans réponse du serveur. Le logo, le nom de l'école, le
  // copyright et les liens du pied restent ceux de la page du personnel.
  famille: {
    couleurs: { principale: '#319190', foncee: '#277574', fond: '#eaf6f5', affiche: '#1f5f5e' },
    connexion: {
      affiche_titre: 'Votre espace famille',
      affiche_texte: "Notes et bulletins, absences, emploi du temps, situation financière et échanges avec l'école : la scolarité de vos enfants, au même endroit.",
      titre: 'Votre espace famille',
      sous_titre: 'Connectez-vous pour suivre la scolarité de vos enfants.',
      bouton: 'Se connecter'
    },
    activation: {
      affiche_titre: 'Bienvenue dans votre espace',
      affiche_texte: "L'école vous a remis un code d'accès. Il ouvre l'espace de vos enfants, une fois pour toutes : ensuite, votre numéro de téléphone et votre mot de passe suffisent.",
      titre: 'Première connexion',
      sous_titre: "Avec le code d'accès remis par l'école.",
      bouton: 'Créer mon espace',
      aide: "Vous avez déjà un espace pour un autre enfant ? Saisissez le même numéro de téléphone : l'enfant y sera ajouté."
    },
    oubli: {
      titre: 'Mot de passe oublié',
      sous_titre: "Saisissez le code de réinitialisation remis par l'école.",
      bouton: 'Changer mon mot de passe'
    },
    premiere_connexion: {
      titre: 'Première connexion ?',
      texte: "L'école vous remet un code d'accès imprimé. Il vous permet de créer votre espace.",
      lien: "J'ai un code d'accès"
    },
    personnel: { libelle: 'Espace du personnel' },

    // Les liens du pied de page sont ceux du personnel, mais ce que lit un
    // parent lui est propre : ces pop-ups remplacent celles du personnel sur
    // les pages des parents. Une clé absente ici (support, mot de passe)
    // retombe sur la pop-up du personnel.
    popups: {
      aide: {
        titre: 'Aide et contact',
        contenu: "Pour toute question sur votre espace famille - un code qui ne fonctionne pas, un enfant qui n'apparaît pas, un mot de passe perdu - adressez-vous au secrétariat de l'établissement.\n\nC'est l'école qui ouvre les accès des familles : elle seule peut vous remettre un nouveau code."
      },
      cgu: {
        titre: "Conditions d'utilisation",
        contenu: "Votre espace vous donne accès au dossier scolaire de vos enfants : notes, absences, emploi du temps et situation financière.\n\nCet accès est personnel. Ne communiquez ni votre mot de passe ni le code remis par l'école : toute personne qui les détient voit le dossier de votre enfant.\n\nLes règles de vie scolaire et les montants affichés sont ceux de l'établissement : en cas de désaccord, c'est à lui qu'il faut vous adresser."
      },
      confidentialite: {
        titre: 'Politique de confidentialité',
        contenu: "Les informations affichées dans votre espace appartiennent à l'établissement de vos enfants. Elles ne sont ni revendues ni communiquées à des tiers.\n\nVous ne voyez que les enfants que l'école a rattachés à votre espace, et les messages que vous envoyez sont lus par son personnel.\n\nPour faire corriger une information erronée - un nom, une date de naissance, un numéro - écrivez à l'école depuis votre espace."
      }
    }
  },

  // ------------------------------------------------------------------------
  // L'espace enseignant : les deux pages d'entrée que voient les enseignants
  // ------------------------------------------------------------------------
  // Même découpage que `famille`, bloc pour bloc. Réglées depuis le CIK
  // (écran « Espace enseignant », clé `enseignant`, migration CIK 021) ; ces
  // valeurs du code restent le repli, bloc par bloc, et la page doit tenir
  // avec elles seules. Les trois sources (ce bloc, l'écran CIK, la migration
  // 021) doivent garder les mêmes valeurs par défaut. Textes de la maquette
  // validée (`Main.dc.html`) ; terre cuite pour qu'on ne confonde jamais cette
  // porte avec celle du personnel (bleu) ou des familles (vert).
  enseignant: {
    couleurs: { principale: '#a9533f', foncee: '#8c4434', fond: '#fbf1ee', affiche: '#5d2e23' },
    connexion: {
      affiche_titre: 'Votre espace enseignant',
      affiche_texte: "Votre emploi du temps, la liste d'appel de vos classes, le suivi de votre programme et vos observations sur les élèves : votre journée de classe, au même endroit.",
      titre: 'Votre espace enseignant',
      sous_titre: 'Connectez-vous pour retrouver vos classes.',
      bouton: 'Se connecter'
    },
    activation: {
      affiche_titre: 'Bienvenue dans votre espace',
      affiche_texte: "L'école vous a remis un code d'accès. Il ouvre votre espace une fois pour toutes : ensuite, votre numéro de téléphone et votre mot de passe suffisent.",
      titre: 'Première connexion',
      sous_titre: "Avec le code d'accès remis par l'école.",
      bouton: 'Créer mon espace',
      aide: "Ce numéro devient votre identifiant de connexion. Votre fiche à l'école ne change pas."
    },
    // Contrairement aux parents, l'affiche change aussi : elle explique qu'un
    // mot de passe perdu passe par le secrétariat.
    oubli: {
      affiche_titre: 'Votre espace enseignant',
      affiche_texte: 'Un mot de passe perdu ne se récupère pas par SMS : le secrétariat vous remet un code de réinitialisation, valable une seule fois.',
      titre: 'Mot de passe oublié',
      sous_titre: "Saisissez le code de réinitialisation remis par l'école.",
      bouton: 'Changer mon mot de passe'
    },
    premiere_connexion: {
      titre: 'Première connexion ?',
      texte: "L'école vous remet un code d'accès imprimé. Il vous permet de créer votre espace.",
      lien: "J'ai un code d'accès"
    },
    personnel: { libelle: 'Espace du personnel' },

    // Comme pour les parents : les liens du pied sont ceux du personnel, ces
    // pop-ups remplacent les siennes sur les pages des enseignants. Une clé
    // absente ici retombe sur la pop-up du personnel.
    popups: {
      aide: {
        titre: 'Aide et contact',
        contenu: "Pour toute question sur votre espace enseignant - un code qui ne fonctionne pas, une classe qui n'apparaît pas, un mot de passe perdu - adressez-vous au secrétariat de l'établissement.\n\nC'est l'école qui ouvre les accès des enseignants : elle seule peut vous remettre un nouveau code."
      },
      cgu: {
        titre: "Conditions d'utilisation",
        contenu: "Votre espace vous donne accès aux classes que l'école vous a confiées pour l'année, et aux informations utiles à votre enseignement.\n\nCet accès est personnel. Ne communiquez ni votre mot de passe ni le code remis par l'école : toute personne qui les détient voit les élèves de vos classes.\n\nCe que vous lisez sur les élèves reste dans l'établissement : il ne se recopie ni ne se partage en dehors."
      },
      confidentialite: {
        titre: 'Politique de confidentialité',
        contenu: "Les informations affichées dans votre espace appartiennent à l'établissement. Elles ne sont ni revendues ni communiquées à des tiers.\n\nVous ne voyez que les classes que l'école vous a attribuées pour l'année en cours.\n\nPour faire corriger une information de votre fiche - un nom, un numéro, une adresse e-mail - adressez-vous au secrétariat : c'est l'école qui la tient."
      }
    }
  }
};

// Un cache par nom d'hôte : localStorage est déjà cloisonné par origine, mais
// deux écoles derrière un même hôte (développement) ne doivent pas se mélanger.
const cleCache = () => `klasysPageConnexion:${window.location.hostname}`;

/**
 * Fusion bloc par bloc d'un espace (famille, enseignant) : un CIK qui n'aurait
 * réglé que les couleurs laisse les textes du code, et inversement.
 * Les pop-ups ont un niveau de plus : un CIK qui n'aurait réglé que le titre
 * d'une pop-up garde le contenu écrit dans le code.
 */
function fusionnerEspace(defauts, recu) {
  return Object.fromEntries(
    Object.keys(defauts).map((bloc) => [
      bloc,
      bloc === 'popups'
        ? Object.fromEntries(
            Object.keys(defauts.popups).map((cle) => [
              cle,
              { ...defauts.popups[cle], ...(recu?.popups?.[cle] || {}) }
            ])
          )
        : { ...defauts[bloc], ...(recu?.[bloc] || {}) }
    ])
  );
}

/**
 * Fusionne une configuration reçue avec les valeurs du code.
 * Une clé absente ou malformée garde la valeur du code : jamais de trou.
 */
export function fusionner(config = {}) {
  const d = CONTENUS_PAR_DEFAUT;
  const textes = Array.isArray(config.textes) && config.textes.some((t) => t?.titre)
    ? config.textes.filter((t) => t?.titre)
    : d.textes;
  return {
    marque: { ...d.marque, ...(config.marque || {}) },
    textes,
    connexion: { ...d.connexion, ...(config.connexion || {}) },
    pied: {
      copyright: config.pied?.copyright ?? d.pied.copyright,
      liens: Array.isArray(config.pied?.liens) ? config.pied.liens : d.pied.liens
    },
    popups: Object.fromEntries(
      Object.keys(d.popups).map((cle) => [cle, { ...d.popups[cle], ...(config.popups?.[cle] || {}) }])
    ),
    // Les deux espaces se fusionnent de la même façon, bloc par bloc : un
    // réglage partiel du CIK laisse le reste aux valeurs du code.
    famille: fusionnerEspace(d.famille, config.famille),
    enseignant: fusionnerEspace(d.enseignant, config.enseignant)
  };
}

/** La dernière réponse gardée pour cette école, ou null. */
export function lireCache() {
  try {
    const brut = localStorage.getItem(cleCache());
    return brut ? JSON.parse(brut) : null;
  } catch {
    return null;
  }
}

export function ecrireCache(donnees) {
  try {
    localStorage.setItem(cleCache(), JSON.stringify(donnees));
  } catch {
    /* navigation privée ou stockage plein : on s'en passe */
  }
}

/**
 * Interroge le serveur. Renvoie `{ config, ecole }` ou null.
 *
 * `anonyme` : pour les pages d'un espace qui a sa propre session (enseignant).
 * La route est publique et n'a besoin que de l'école ; on n'y joint donc ni le
 * jeton du personnel, ni son année, ni son cookie de session — sur un poste
 * partagé, la page d'un enseignant ne doit rien envoyer au nom de la
 * secrétaire restée connectée. Sans l'option, rien ne change pour la page du
 * personnel ni pour l'espace famille.
 */
export async function chargerContenus({ anonyme = false } = {}) {
  try {
    // `getAuthHeaders` porte l'en-tête X-Tenant tiré du sous-domaine, avec la
    // même règle que le reste de l'application (localhost → www). En appel
    // anonyme, on ne l'appelle même pas — elle lit la clé `token` — et l'on
    // pose l'école seule, tirée de l'hôte par la même règle.
    const ecole = anonyme ? tenantDeLHote() : null;
    const entetes = anonyme ? (ecole ? { 'X-Tenant': ecole } : {}) : getAuthHeaders();
    const reponse = await fetch('/api/public/page-connexion', {
      headers: entetes,
      ...(anonyme ? { credentials: 'omit' } : {})
    });
    if (!reponse.ok) return null;
    const corps = await reponse.json();
    return corps?.success ? corps.data : null;
  } catch {
    return null;
  }
}
